/**
 * azureControls.ts - Catalogue des contrôles "NIS Azure"
 *
 * Application des exigences CyFun (CCB) + NIS2 + ISO 27001 a une subscription
 * Azure. 40 contrôles MVP repartis en 7 familles d infrastructure cloud.
 *
 * Modele :
 *  - Chaque contrôle expose `armEndpoints` : endpoints REST ARM utilisés par le
 *    worker RunAzureScan pour la verification automatique.
 *  - `manual: true` quand pas d endpoint ARM standard (defi : audit Subscription
 *    Owner contact info, etc.)
 *  - `severity` pondere le score global comme pour les contrôles M365.
 *  - References frameworks systematiques : NIS2 Art.21 + CyFun + ISO 27001 A.*
 */

import type { Severity, ControlReference } from './types';

/** Familles fonctionnelles de l audit Azure (orthogonales aux familles M365). */
export type AzureFamily =
  | 'identity'     // Identite & RBAC sur la subscription Azure
  | 'network'      // VNets, NSGs, Firewall, JIT, Bastion
  | 'data'         // Storage, SQL, Cosmos, Backup
  | 'compute'      // VMs, App Service, AKS, Container Apps
  | 'keyvault'     // Azure Key Vault + secrets/keys mgmt
  | 'logging'      // Activity Logs, Diagnostic Settings, Defender, Sentinel
  | 'governance';  // Azure Policy, Tags, Locks, Subscriptions

/** Niveau d effort de remediation - pondere le plan d action. */
export type RemediationEffort = 'low' | 'medium' | 'high';

/** Frequence d audit recommandée. */
export type AuditFrequency = 'continuous' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually';

/** Definition d un contrôle Azure (catalogue). v2026.06.128 schema enrichi. */
export interface AzureControlDefinition {
  /** Id stable, prefixe par famille : AZ-ID-001, AZ-NET-002, etc. */
  id: string;
  family: AzureFamily;
  title: string;
  description: string;
  rationale: string;
  severity: Severity;
  references: ControlReference[];
  /** Endpoints ARM REST cibles (pour transparence dans le rapport). */
  armEndpoints: string[];
  /** Recommandation textuelle si le contrôle echoue. */
  remediation: string;
  /** True si pas d API ARM standard - audit manuel. */
  manual: boolean;
  /** Lien deep vers le portail Azure pour verifier le contrôle a la main. */
  azurePortalUrl?: string;

  // --- v2026.06.128 : Champs enrichis pour rapport executif + audit externe ---

  /** Preuve auditable a presenter en audit externe (ex: capture portail, export JSON ARM, ticket de changement). */
  evidence?: string;
  /** Impact business concret si le contrôle echoue. Phrase courte orientee dirigeant. */
  impactIfFails?: string;
  /** Effort de remediation (priorise le plan d action). */
  effort?: RemediationEffort;
  /** Frequence recommandée de revue / re-audit du contrôle. */
  frequency?: AuditFrequency;
  /**
   * Mapping vers Microsoft Defender for Cloud recommendation ID (UUID v4).
   * Permet de croiser avec le score Defender existant chez le client.
   */
  defenderRecommendationId?: string;
}

// ============================================================================
//                           CATALOGUE 40 CONTROLES MVP
// ============================================================================

export const AZURE_CONTROLS: AzureControlDefinition[] = [

  // ==========================================================================
  //  IDENTITY & RBAC (AZ-ID-*) - 6 contrôles
  // ==========================================================================
  {
    id: 'AZ-ID-001', family: 'identity', severity: 'critical',
    title: 'Tous les Owners de subscription ont MFA active',
    description: 'Aucun utilisateur avec le role "Owner" sur la subscription ne se connecte sans MFA.',
    rationale: 'Un Owner sans MFA est un point de defaillance unique - compromission de mot de passe = contrôle total de la subscription, vol de données, ransomware.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(j)', label: 'Authentification multi-facteur' },
      { framework: 'ISO27001', ref: 'A.5.16',        label: 'Gestion des identités' },
      { framework: 'CyFun',    ref: 'PR.AC-1',       label: 'Identités verifiees', cyfunLevel: 'Basic' },
      { framework: 'CyFun',    ref: 'PR.AC-7.3',     label: 'MFA pour comptes a privileges', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '1.1',           label: 'Security Defaults / MFA enforced' },
    ],
    armEndpoints: [
      'GET /subscriptions/{subId}/providers/Microsoft.Authorization/roleAssignments?$filter=roleDefinitionId eq Owner',
      'GET /v1.0/users/{userId}/authentication/methods (Graph - check MFA methods)',
    ],
    remediation: 'Portail Azure > Subscriptions > IAM > Lister les Owners. Vérifier chaque user dans Entra ID > Authentication methods. Activer MFA + idealement passer en passwordless (FIDO2/Authenticator).',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#blade/Microsoft_Azure_Subscriptions/SubscriptionsBlade',
    evidence: 'Export JSON des role assignments + capture portail Entra ID > Authentication methods pour chaque compte privilégié.',
    impactIfFails: 'Compromission d identite -> acces non autorise a toute la subscription, ransomware, exfiltration.',
    effort: 'low',
    frequency: 'continuous',
    defenderRecommendationId: '94290b00-4d0c-d7b4-7cea-064a9554e681',
  },
  {
    id: 'AZ-ID-002', family: 'identity', severity: 'high',
    title: 'Pas plus de 3 Owners actifs sur la subscription',
    description: 'Le nombre d Owners est limite (3 max recommande, idealement 2). Les administrations doivent passer par PIM (Just-In-Time).',
    rationale: 'Chaque Owner permanent est un risque de compromission. Reduction du nombre = principle of least privilege. CIS Azure 1.2.',
    references: [
      { framework: 'ISO27001', ref: 'A.5.15',     label: 'Contrôle d accès' },
      { framework: 'ISO27001', ref: 'A.5.18',     label: 'Droits d accès' },
      { framework: 'CyFun',    ref: 'PR.AC-4',    label: 'Permissions managed (least privilege)', cyfunLevel: 'Basic' },
      { framework: 'CIS-Azure', ref: '1.8',           label: 'Subscription owners <= 3' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.Authorization/roleAssignments'],
    remediation: 'Réduire le nombre d Owners permanents a 2-3 max. Pour les autres : passer en role "Contributor" + utilisation PIM (eligible Owner) pour escalade Just-In-Time.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#blade/Microsoft_Azure_PIMCommon/CommonMenuBlade',
    evidence: 'Export JSON des role assignments + capture portail Entra ID > Authentication methods pour chaque compte privilégié.',
    impactIfFails: 'Compromission d identite -> acces non autorise a toute la subscription, ransomware, exfiltration.',
    effort: 'medium',
    frequency: 'weekly',
  },
  {
    id: 'AZ-ID-003', family: 'identity', severity: 'high',
    title: 'Privileged Identity Management (PIM) actif sur la subscription',
    description: 'PIM est configure pour les roles privilégiés de la subscription (Owner, Contributor) avec activation Just-In-Time + approbation + MFA.',
    rationale: 'Empeche les comptes permanents en role privilégié. Réduit la fenêtre d attaque drastiquement. Exigence CyFun Important.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(j)', label: 'Gestion des accès privilégiés' },
      { framework: 'ISO27001', ref: 'A.5.18',        label: 'Droits d accès' },
      { framework: 'CyFun',    ref: 'PR.AC-4.7',     label: 'JIT pour comptes privileges', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '1.10',           label: 'PIM JIT eligible for privileged roles' },
    ],
    armEndpoints: [
      'GET /providers/Microsoft.Authorization/roleManagementPolicies (PIM policies)',
      'GET /providers/Microsoft.Authorization/roleEligibilityScheduleInstances',
    ],
    remediation: 'Entra ID > Privileged Identity Management > Azure resources > Selectionner subscription > Settings. Activer JIT + max activation 8h + approbation requise + MFA required + justification obligatoire.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#blade/Microsoft_Azure_PIMCommon/ResourceMenuBlade/aadroles',
    evidence: 'Export JSON des role assignments + capture portail Entra ID > Authentication methods pour chaque compte privilégié.',
    impactIfFails: 'Compromission d identite -> acces non autorise a toute la subscription, ransomware, exfiltration.',
    effort: 'medium',
    frequency: 'weekly',
  },
  {
    id: 'AZ-ID-004', family: 'identity', severity: 'high',
    title: 'Pas de secrets service principal expirants (> 1 an)',
    description: 'Les Service Principals utilisés pour automation (CI/CD, Terraform) ont des secrets/certificats avec rotation < 12 mois.',
    rationale: 'Un secret SP de 3+ ans est presque garanti d être dans un repo Git oublie. Rotation régulière = principle de defense en profondeur.',
    references: [
      { framework: 'ISO27001', ref: 'A.8.24',     label: 'Gestion des cles' },
      { framework: 'ISO27001', ref: 'A.5.17',     label: 'Authentification' },
      { framework: 'CyFun',    ref: 'PR.AC-1',    label: 'Identities managed', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '1.11',           label: 'Service principals secret rotation' },
    ],
    armEndpoints: ['GET /v1.0/applications (Graph - check passwordCredentials.endDateTime)'],
    remediation: 'Lister les Service Principals avec secrets > 1 an. Pour chacun : vérifier usage, rotater le secret OU migrer vers Managed Identity / Workload Identity Federation (preferable).',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade',
    evidence: 'Export JSON des role assignments + capture portail Entra ID > Authentication methods pour chaque compte privilégié.',
    impactIfFails: 'Compromission d identite -> acces non autorise a toute la subscription, ransomware, exfiltration.',
    effort: 'medium',
    frequency: 'weekly',
  },
  {
    id: 'AZ-ID-005', family: 'identity', severity: 'medium',
    title: 'Conditional Access bloque l accès hors entreprise au portal Azure',
    description: 'Une politique Entra Conditional Access restreint l accès a portal.azure.com aux emplacements approuves (siège, VPN) OU exige MFA + device compliant.',
    rationale: 'Limite la surface d attaque : un attaquant avec credentials valides ne peut acceder au portail depuis nimporte ou. Standard zero-trust.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(j)', label: 'Conditional Access' },
      { framework: 'ISO27001', ref: 'A.5.15',        label: 'Contrôle d accès' },
      { framework: 'CyFun',    ref: 'PR.AC-3',       label: 'Remote access managed', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '1.14',           label: 'Restrict admin portals via CA' },
    ],
    armEndpoints: ['GET /v1.0/identity/conditionalAccess/policies (Graph)'],
    remediation: 'Entra ID > Conditional Access > Create policy : "Block-Azure-Portal-Untrusted". Conditions : Cloud apps = Microsoft Azure Management. Locations = exclude Trusted IPs. Grant : Block OR Require MFA + Compliant device.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/Microsoft_AAD_ConditionalAccess/ConditionalAccessBlade',
    evidence: 'Export JSON des role assignments + capture portail Entra ID > Authentication methods pour chaque compte privilégié.',
    impactIfFails: 'Compromission d identite -> acces non autorise a toute la subscription, ransomware, exfiltration.',
    effort: 'medium',
    frequency: 'monthly',
  },
  {
    id: 'AZ-ID-006', family: 'identity', severity: 'medium',
    title: 'Compte break-glass documente et protege',
    description: 'Un (ou 2) compte(s) "break-glass" cloud-only (UPN xxx.onmicrosoft.com) avec MFA + password long en coffre, exclu des politiques CA, monitore.',
    rationale: 'Permet de reprendre la main si MFA / CA cassent (mauvaise config, panne IdP federation). Exigence Microsoft Best Practices + CIS Azure 1.4.',
    references: [
      { framework: 'ISO27001', ref: 'A.5.16',     label: 'Gestion des identités' },
      { framework: 'CyFun',    ref: 'PR.IP-9',    label: 'Response plans (break-glass)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '1.12',           label: 'Cloud-only break-glass account' },
    ],
    armEndpoints: [],
    remediation: 'Créer 1-2 comptes "breakglass1@<tenant>.onmicrosoft.com" : Global Admin, MFA Authenticator app + password 64 chars en coffre physique, exclu de TOUTES les CA policies, alert sur sign-in via Sentinel/Defender.',
    manual: true,
    azurePortalUrl: 'https://portal.azure.com/#view/Microsoft_AAD_UsersAndTenants/UserManagementMenuBlade',
    evidence: 'Export JSON des role assignments + capture portail Entra ID > Authentication methods pour chaque compte privilégié.',
    impactIfFails: 'Compromission d identite -> acces non autorise a toute la subscription, ransomware, exfiltration.',
    effort: 'medium',
    frequency: 'monthly',
  },

  // ==========================================================================
  //  NETWORK (AZ-NET-*) - 7 contrôles
  // ==========================================================================
  {
    id: 'AZ-NET-001', family: 'network', severity: 'critical',
    title: 'Aucun NSG n autorise RDP/SSH depuis Internet (0.0.0.0/0)',
    description: 'Tous les Network Security Groups verifient : pas de règle "Allow" sur ports 22 (SSH) ou 3389 (RDP) avec source = Any/Internet/0.0.0.0/0.',
    rationale: 'L expostion RDP/SSH internet est attaquee en continu (bots). Compromission = quelques heures. Critique pour CyFun Basic.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(g)', label: 'Hygiene cyber' },
      { framework: 'ISO27001', ref: 'A.8.20',        label: 'Sécurité des réseaux' },
      { framework: 'ISO27001', ref: 'A.8.22',        label: 'Segmentation réseau' },
      { framework: 'CyFun',    ref: 'PR.AC-5',       label: 'Network integrity protected', cyfunLevel: 'Basic' },
      { framework: 'CIS-Azure', ref: '6.1+6.2',           label: 'No RDP/SSH from Internet' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.Network/networkSecurityGroups'],
    remediation: 'Pour chaque NSG : revoir les inbound rules. Supprimer toute règle Allow sur 22/3389 depuis Internet. Acceder aux VMs via Bastion / VPN / JIT VM Access uniquement.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.Network%2FnetworkSecurityGroups',
    evidence: 'Export ARM de la configuration (NSG rules, Bastion config, JIT requests) + capture portail Network Security.',
    impactIfFails: 'Exposition réseau directe -> reconnaissance et pivot d attaquant facilites, services compromis.',
    effort: 'low',
    frequency: 'continuous',
    defenderRecommendationId: 'b2c3e8d4-5f9a-4c8d-b6e8-7f5a4b9c2e6d',
  },
  {
    id: 'AZ-NET-002', family: 'network', severity: 'high',
    title: 'Azure Firewall ou WAF protegent le périmètre',
    description: 'Au moins un Azure Firewall (ou tier Premium d Application Gateway avec WAF) protege l ingress du tenant Azure.',
    rationale: 'Première ligne de defense pour services exposés. Sans WAF : SQLi, XSS, OWASP Top 10 non filtres. NIS2 Art. 21(2)(g).',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(g)', label: 'Hygiene cyber' },
      { framework: 'ISO27001', ref: 'A.8.20',        label: 'Sécurité des réseaux' },
      { framework: 'ISO27001', ref: 'A.8.23',        label: 'Filtrage web' },
      { framework: 'CyFun',    ref: 'PR.PT-4',       label: 'Communications protected', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '6.6',           label: 'Network firewall / WAF configured' },
    ],
    armEndpoints: [
      'GET /subscriptions/{subId}/providers/Microsoft.Network/azureFirewalls',
      'GET /subscriptions/{subId}/providers/Microsoft.Network/applicationGateways',
    ],
    remediation: 'Déployer Azure Firewall (Standard ou Premium) sur le hub VNet. Pour apps web exposees : Application Gateway WAF v2 avec OWASP 3.2 ruleset. Bloquer all + allowlist explicite.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.Network%2FazureFirewalls',
    evidence: 'Export ARM de la configuration (NSG rules, Bastion config, JIT requests) + capture portail Network Security.',
    impactIfFails: 'Exposition réseau directe -> reconnaissance et pivot d attaquant facilites, services compromis.',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: '34debc8f-a82a-69a7-ed03-2c08fce7e76e',
  },
  {
    id: 'AZ-NET-003', family: 'network', severity: 'high',
    title: 'Just-In-Time (JIT) VM access active sur les VMs sensibles',
    description: 'Les VMs critiques ont JIT VM Access configure : ports 22/3389 fermes par defaut, ouverts a la demande pour <= 3h après approbation.',
    rationale: 'Réduit fenêtre d attaque a quasi-zero. Coup d arret aux bots qui scannent Internet. Nécessite Defender for Cloud (Defender for Servers plan).',
    references: [
      { framework: 'ISO27001', ref: 'A.8.20',     label: 'Sécurité réseau' },
      { framework: 'CyFun',    ref: 'PR.AC-3',    label: 'Remote access managed', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '7.6',           label: 'JIT VM access' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.Security/jitNetworkAccessPolicies'],
    remediation: 'Defender for Cloud > Workload protections > Just-in-time VM access > Configurer chaque VM > Ports 22, 3389 > Max request hours = 3. Nécessite tier Defender for Servers P1+.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/Microsoft_Azure_Security/SecurityMenuBlade/~/16',
    evidence: 'Export ARM de la configuration (NSG rules, Bastion config, JIT requests) + capture portail Network Security.',
    impactIfFails: 'Exposition réseau directe -> reconnaissance et pivot d attaquant facilites, services compromis.',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: '8606fc78-9f54-4c2a-a73f-12c40bf9cd9d',
  },
  {
    id: 'AZ-NET-004', family: 'network', severity: 'medium',
    title: 'IPs publiques minimisees (pas de VM directement exposee)',
    description: 'Aucune VM n a d IP publique directe. Tout passe par Load Balancer / Application Gateway / Front Door avec backend pool prive.',
    rationale: 'Limite l empreinte attaquable. Une VM directement exposee = surface d attaque permanente.',
    references: [
      { framework: 'ISO27001', ref: 'A.8.20',     label: 'Sécurité des réseaux' },
      { framework: 'CyFun',    ref: 'PR.AC-5',    label: 'Network integrity', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '6.5',           label: 'Minimize public IP exposure' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.Network/publicIPAddresses'],
    remediation: 'Lister les Public IPs. Pour chaque IP attachee a une VM : migrer vers architecture front-end (Load Balancer/AGW/Front Door) + back-end VM en VNet prive. Supprimer les Public IPs orphelines.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.Network%2FpublicIPAddresses',
    evidence: 'Export ARM de la configuration (NSG rules, Bastion config, JIT requests) + capture portail Network Security.',
    impactIfFails: 'Exposition réseau directe -> reconnaissance et pivot d attaquant facilites, services compromis.',
    effort: 'medium',
    frequency: 'monthly',
  },
  {
    id: 'AZ-NET-005', family: 'network', severity: 'medium',
    title: 'Azure Bastion deploye pour accès VM sécurisé',
    description: 'Un Bastion est deploye sur le hub VNet pour accès RDP/SSH aux VMs sans exposition Internet et sans VPN client.',
    rationale: 'Méthode preferee Microsoft pour accès VM admin. Audit log integre. Tunneling browser-based, pas de client special.',
    references: [
      { framework: 'ISO27001', ref: 'A.8.20',     label: 'Sécurité réseau' },
      { framework: 'CyFun',    ref: 'PR.AC-3',    label: 'Remote access managed', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '6.6',           label: 'Bastion for secure VM access (variant)' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.Network/bastionHosts'],
    remediation: 'Azure Portal > Bastions > Create > Tier "Standard" minimum (audit log + shareable links). Deploiement sur le subnet "AzureBastionSubnet" du hub VNet. Cout : ~150€/mois.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.Network%2FbastionHosts',
    evidence: 'Export ARM de la configuration (NSG rules, Bastion config, JIT requests) + capture portail Network Security.',
    impactIfFails: 'Exposition réseau directe -> reconnaissance et pivot d attaquant facilites, services compromis.',
    effort: 'medium',
    frequency: 'monthly',
    defenderRecommendationId: '34debc8f-a82a-69a7-ed03-2c08fce7e76e',
  },
  {
    id: 'AZ-NET-006', family: 'network', severity: 'medium',
    title: 'Network Watcher active dans toutes les regions utilisees',
    description: 'Network Watcher est enabled dans chaque region ou des ressources Azure tournent (auto-enable par defaut depuis 2019).',
    rationale: 'Permet flow logs NSG + diagnostic réseau + topology. Sans NW, impossible d enqueter sur incidents réseau.',
    references: [
      { framework: 'ISO27001', ref: 'A.8.16',     label: 'Activites de surveillance' },
      { framework: 'CyFun',    ref: 'DE.CM-1',    label: 'Network monitored', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '6.4',           label: 'Network Watcher enabled' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.Network/networkWatchers'],
    remediation: 'Network Watchers > List > Vérifier presence pour chaque region utilisee. Si manquant : Network Watchers > Add > <region>. Cout : gratuit (sauf storage des flow logs).',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/Microsoft_Azure_Network/NetworkWatcherMenuBlade',
    evidence: 'Export ARM de la configuration (NSG rules, Bastion config, JIT requests) + capture portail Network Security.',
    impactIfFails: 'Exposition réseau directe -> reconnaissance et pivot d attaquant facilites, services compromis.',
    effort: 'medium',
    frequency: 'monthly',
  },
  {
    id: 'AZ-NET-007', family: 'network', severity: 'medium',
    title: 'DDoS Protection Standard sur VNets critiques',
    description: 'Les VNets hebergeant des services publics ont DDoS Protection Standard (vs Basic). Cout : ~2700€/mois par tenant.',
    rationale: 'Mitigation DDoS L3/L4 + alertes + telemetry. Critique pour OIV / OSE / services publics exposés.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(g)', label: 'Hygiene cyber' },
      { framework: 'ISO27001', ref: 'A.8.20',        label: 'Sécurité réseau' },
      { framework: 'CyFun',    ref: 'PR.IP-9',       label: 'DDoS resilience', cyfunLevel: 'Essential' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.Network/ddosProtectionPlans'],
    remediation: 'Networking > DDoS Protection Plans > Create. Cout fixe ~2700€/mois (couvre tous VNets du tenant). Associer aux VNets exposés via "DDoS protection : Enable" sur la VNet.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.Network%2FddosProtectionPlans',
    evidence: 'Export ARM de la configuration (NSG rules, Bastion config, JIT requests) + capture portail Network Security.',
    impactIfFails: 'Exposition réseau directe -> reconnaissance et pivot d attaquant facilites, services compromis.',
    effort: 'medium',
    frequency: 'monthly',
  },

  // ==========================================================================
  //  STORAGE & DATA (AZ-DAT-*) - 7 contrôles
  // ==========================================================================
  {
    id: 'AZ-DAT-001', family: 'data', severity: 'critical',
    title: 'Storage accounts interdisent l accès public anonyme',
    description: 'Tous les Storage Accounts ont "Allow Blob anonymous access" = Disabled, et aucun container avec public access != Private.',
    rationale: 'Top 3 des breaches publics 2023-2024 = blob public oublie. Critique. Audit indispensable.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(b)', label: 'Gestion incidents - prevention' },
      { framework: 'ISO27001', ref: 'A.5.10',        label: 'Usage acceptable' },
      { framework: 'CyFun',    ref: 'PR.DS-5',       label: 'Data leak prevention', cyfunLevel: 'Basic' },
      { framework: 'CIS-Azure', ref: '3.5',           label: 'Disallow public blob access' },
    ],
    armEndpoints: [
      'GET /subscriptions/{subId}/providers/Microsoft.Storage/storageAccounts',
      'GET /subscriptions/{subId}/providers/Microsoft.Storage/storageAccounts/{name}/blobServices/default/containers',
    ],
    remediation: 'Pour chaque Storage Account : Configuration > Allow Blob anonymous access = Disabled. Pour chaque container existant : Change access level > Private. Vérifier les apps qui pourraient casser.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.Storage%2FstorageAccounts',
    evidence: 'Export JSON des proprietes ressource (encryption, network ACLs, retention) + capture portail Storage/SQL/Cosmos.',
    impactIfFails: 'Fuite ou alteration de données clients -> sanctions RGPD, NIS2 (jusqu a 10 M EUR), perte de confiance.',
    effort: 'low',
    frequency: 'continuous',
    defenderRecommendationId: '8c3e93d3-0276-4d06-b20a-9a9f3012742c',
  },
  {
    id: 'AZ-DAT-002', family: 'data', severity: 'high',
    title: 'Storage accounts exigent TLS 1.2+ minimum',
    description: 'Tous les Storage Accounts ont "Minimum TLS version" = 1.2 (ou 1.3 si dispo). Aucun ne tolere TLS 1.0/1.1.',
    rationale: 'TLS 1.0/1.1 deprecated par PCI DSS 3.2 + ANSSI + NIS2. TLS 1.2+ est le minimum acceptable industriel.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(h)', label: 'Cryptographie' },
      { framework: 'ISO27001', ref: 'A.8.24',        label: 'Utilisation cryptographie' },
      { framework: 'CyFun',    ref: 'PR.DS-2',       label: 'Data in transit protected', cyfunLevel: 'Basic' },
      { framework: 'CIS-Azure', ref: '3.1',           label: 'Secure transfer required' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.Storage/storageAccounts'],
    remediation: 'Pour chaque Storage Account : Configuration > Minimum TLS version = 1.2. Vérifier ressources amont (apps, scripts) compatibles.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.Storage%2FstorageAccounts',
    evidence: 'Export JSON des proprietes ressource (encryption, network ACLs, retention) + capture portail Storage/SQL/Cosmos.',
    impactIfFails: 'Fuite ou alteration de données clients -> sanctions RGPD, NIS2 (jusqu a 10 M EUR), perte de confiance.',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: '8c3e93d3-0276-4d06-b20a-9a9f3012742c',
  },
  {
    id: 'AZ-DAT-003', family: 'data', severity: 'medium',
    title: 'Storage accounts utilisent Customer-Managed Keys (CMK)',
    description: 'Pour les Storage Accounts contenant données sensibles (RGPD, sante, finance) : chiffrement avec CMK via Key Vault.',
    rationale: 'Defaut Microsoft Service Managed Keys. CMK = contrôle souverain : revocation possible, audit access key, conformité secteur regule.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(h)', label: 'Cryptographie' },
      { framework: 'ISO27001', ref: 'A.8.24',        label: 'Gestion des cles' },
      { framework: 'CyFun',    ref: 'PR.DS-1',       label: 'Data at rest protected', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '3.8',           label: 'Storage encryption with CMK' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.Storage/storageAccounts'],
    remediation: 'Pour Storage Account sensible : Encryption > Customer-managed keys > Pointer vers Key Vault. Nécessite Key Vault avec Soft Delete + Purge Protection. Pas de cout additionnel.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.Storage%2FstorageAccounts',
    evidence: 'Export JSON des proprietes ressource (encryption, network ACLs, retention) + capture portail Storage/SQL/Cosmos.',
    impactIfFails: 'Fuite ou alteration de données clients -> sanctions RGPD, NIS2 (jusqu a 10 M EUR), perte de confiance.',
    effort: 'medium',
    frequency: 'monthly',
    defenderRecommendationId: '8c3e93d3-0276-4d06-b20a-9a9f3012742c',
  },
  {
    id: 'AZ-DAT-004', family: 'data', severity: 'high',
    title: 'SQL Databases ont Transparent Data Encryption (TDE) actif',
    description: 'Toutes les Azure SQL DB / Managed Instances ont TDE = Enabled (par defaut depuis 2017 mais a vérifier).',
    rationale: 'Chiffrement au repos transparent pour les apps. Active par defaut nouvelles DB mais d anciennes DB peuvent avoir TDE off.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(h)', label: 'Cryptographie' },
      { framework: 'ISO27001', ref: 'A.8.24',        label: 'Cryptographie' },
      { framework: 'CyFun',    ref: 'PR.DS-1',       label: 'Data at rest protected', cyfunLevel: 'Basic' },
      { framework: 'CIS-Azure', ref: '4.3',           label: 'SQL TDE enabled' },
    ],
    armEndpoints: [
      'GET /subscriptions/{subId}/providers/Microsoft.Sql/servers',
      'GET /subscriptions/{subId}/providers/Microsoft.Sql/servers/{srv}/databases/{db}/transparentDataEncryption',
    ],
    remediation: 'SQL DB > Security > Transparent data encryption > Data encryption = ON. Pour CMK : pointer vers Key Vault + activer Managed Identity sur SQL Server.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.Sql%2Fservers',
    evidence: 'Export JSON des proprietes ressource (encryption, network ACLs, retention) + capture portail Storage/SQL/Cosmos.',
    impactIfFails: 'Fuite ou alteration de données clients -> sanctions RGPD, NIS2 (jusqu a 10 M EUR), perte de confiance.',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: 'f8262030-eafe-4d05-87f4-0fbf85a05c1e',
  },
  {
    id: 'AZ-DAT-005', family: 'data', severity: 'high',
    title: 'SQL Database auditing actif et envoye vers Log Analytics',
    description: 'Toutes les Azure SQL DB ont l audit active, retention >= 90 jours, et envoient vers Log Analytics workspace OU Storage.',
    rationale: 'Sans audit SQL : aucune trace d accès aux données. Critique pour incident response RGPD et détection menace interne.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(b)', label: 'Gestion incidents' },
      { framework: 'ISO27001', ref: 'A.8.15',        label: 'Journalisation' },
      { framework: 'CyFun',    ref: 'DE.CM-1',       label: 'Network monitored', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '4.1+4.2',           label: 'SQL auditing on + retention' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.Sql/servers/{srv}/auditingSettings'],
    remediation: 'SQL Server > Auditing > Enable > Selectionner Log Analytics (preferable) ou Storage. Retention >= 90 jours. Audit Action Group "BATCH_COMPLETED_GROUP, SUCCESSFUL_DATABASE_AUTHENTICATION_GROUP, FAILED_DATABASE_AUTHENTICATION_GROUP".',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.Sql%2Fservers',
    evidence: 'Export JSON des proprietes ressource (encryption, network ACLs, retention) + capture portail Storage/SQL/Cosmos.',
    impactIfFails: 'Fuite ou alteration de données clients -> sanctions RGPD, NIS2 (jusqu a 10 M EUR), perte de confiance.',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: 'f8262030-eafe-4d05-87f4-0fbf85a05c1e',
  },
  {
    id: 'AZ-DAT-006', family: 'data', severity: 'medium',
    title: 'Cosmos DB firewall configure (pas d accès publique)',
    description: 'Tous les Cosmos DB accounts ont public network access = Disabled OU IP firewall configure (allowlist explicite).',
    rationale: 'Cosmos DB exposed = data exfiltration potential. Le firewall et VNet intégration sont gratuits.',
    references: [
      { framework: 'ISO27001', ref: 'A.8.22',     label: 'Segmentation réseau' },
      { framework: 'CyFun',    ref: 'PR.AC-5',    label: 'Network integrity', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '4.9',           label: 'Cosmos DB no public network' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.DocumentDB/databaseAccounts'],
    remediation: 'Cosmos DB > Networking > Public network access = "Selected networks". Ajouter VNet subnets + IPs explicites des apps. Pour zero-trust : Private endpoints.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.DocumentDB%2FdatabaseAccounts',
    evidence: 'Export JSON des proprietes ressource (encryption, network ACLs, retention) + capture portail Storage/SQL/Cosmos.',
    impactIfFails: 'Fuite ou alteration de données clients -> sanctions RGPD, NIS2 (jusqu a 10 M EUR), perte de confiance.',
    effort: 'medium',
    frequency: 'monthly',
    defenderRecommendationId: '34debc8f-a82a-69a7-ed03-2c08fce7e76e',
  },
  {
    id: 'AZ-DAT-007', family: 'data', severity: 'medium',
    title: 'Soft delete activated sur tous les Storage Accounts',
    description: 'Tous les Storage Accounts ont Soft delete (blob + container) active avec retention >= 7 jours, idealement 30.',
    rationale: 'Defense ransomware + erreur humaine. Permet de restaurer un blob supprime accidentellement OU par malware.',
    references: [
      { framework: 'ISO27001', ref: 'A.8.13',     label: 'Sauvegarde' },
      { framework: 'CyFun',    ref: 'PR.IP-4',    label: 'Backups maintained', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '3.4',           label: 'Storage soft delete' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.Storage/storageAccounts/{name}/blobServices/default'],
    remediation: 'Storage Account > Data protection > Enable soft delete for blobs (30 days) + Enable soft delete for containers (30 days) + Enable versioning. Optionnel : enable point-in-time restore.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.Storage%2FstorageAccounts',
    evidence: 'Export JSON des proprietes ressource (encryption, network ACLs, retention) + capture portail Storage/SQL/Cosmos.',
    impactIfFails: 'Fuite ou alteration de données clients -> sanctions RGPD, NIS2 (jusqu a 10 M EUR), perte de confiance.',
    effort: 'medium',
    frequency: 'monthly',
    defenderRecommendationId: '8c3e93d3-0276-4d06-b20a-9a9f3012742c',
  },

  // ==========================================================================
  //  COMPUTE (AZ-CMP-*) - 6 contrôles
  // ==========================================================================
  {
    id: 'AZ-CMP-001', family: 'compute', severity: 'high',
    title: 'VMs ont disk encryption active (Azure Disk Encryption ou SSE)',
    description: 'Toutes les VMs ont chiffrement disk active : SSE (par defaut) au minimum, ADE pour data sensible (BitLocker/dm-crypt OS + data disks).',
    rationale: 'Chiffrement au repos exigence NIS2 et ISO. SSE est gratuit + par defaut. ADE nécessite cle Key Vault.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(h)', label: 'Cryptographie' },
      { framework: 'ISO27001', ref: 'A.8.24',        label: 'Cryptographie' },
      { framework: 'CyFun',    ref: 'PR.DS-1',       label: 'Data at rest', cyfunLevel: 'Basic' },
      { framework: 'CIS-Azure', ref: '7.1+7.7',           label: 'VM OS disk CMK + ADE' },
    ],
    armEndpoints: [
      'GET /subscriptions/{subId}/providers/Microsoft.Compute/disks',
      'GET /subscriptions/{subId}/providers/Microsoft.Compute/virtualMachines',
    ],
    remediation: 'Vérifier les Disks : encryption.type = "EncryptionAtRestWithPlatformKey" minimum (SSE). Pour VMs sensibles : extension AzureDiskEncryption (full disk avec cle Key Vault).',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.Compute%2FvirtualMachines',
    evidence: 'Export ARM de la configuration VM/App Service (disk encryption, system updates, identity) + capture portail.',
    impactIfFails: 'Workload compromis -> deploiement de malware, minage crypto, pivot vers d autres ressources.',
    effort: 'medium',
    frequency: 'weekly',
  },
  {
    id: 'AZ-CMP-002', family: 'compute', severity: 'high',
    title: 'VMs ont Microsoft Defender for Servers (MDE) agent installe',
    description: 'Les VMs Windows et Linux ont Defender for Cloud "Defender for Servers" P1 ou P2 active, agent MDE/MMA deploye.',
    rationale: 'EDR critique pour détecter ransomware, persistence, lateral movement. CyFun Important pour entités NIS2.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(g)', label: 'Hygiene cyber' },
      { framework: 'ISO27001', ref: 'A.8.7',         label: 'Protection malware' },
      { framework: 'CyFun',    ref: 'DE.CM-4',       label: 'Malicious code detected', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '2.1.5',           label: 'Defender for Servers enabled' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.Security/pricings'],
    remediation: 'Defender for Cloud > Environment settings > Subscription > Defender plans > Servers > Plan 1 ou Plan 2 (Plan 2 recommande pour fonctionnalites avancees). Auto-provision = ON.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/Microsoft_Azure_Security/SecurityMenuBlade/~/0',
    evidence: 'Export ARM de la configuration VM/App Service (disk encryption, system updates, identity) + capture portail.',
    impactIfFails: 'Workload compromis -> deploiement de malware, minage crypto, pivot vers d autres ressources.',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: 'b1cd27e0-2ecb-4222-9d27-542e7d3ec4d6',
  },
  {
    id: 'AZ-CMP-003', family: 'compute', severity: 'high',
    title: 'App Services / Function Apps exigent HTTPS only',
    description: 'Toutes les Web Apps, Function Apps, Logic Apps ont "HTTPS only" = ON et "Minimum TLS version" = 1.2+.',
    rationale: 'Empeche transit HTTP non chiffre. Critique pour données PII / auth tokens. Standard PCI DSS.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(h)', label: 'Cryptographie' },
      { framework: 'ISO27001', ref: 'A.8.20',        label: 'Sécurité réseau' },
      { framework: 'CyFun',    ref: 'PR.DS-2',       label: 'Data in transit', cyfunLevel: 'Basic' },
      { framework: 'CIS-Azure', ref: '9.1',           label: 'App Service HTTPS only' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.Web/sites'],
    remediation: 'Pour chaque App Service : Configuration > General settings > HTTPS Only = ON + Minimum TLS Version = 1.2. Vérifier les redirections custom.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.Web%2Fsites',
    evidence: 'Export ARM de la configuration VM/App Service (disk encryption, system updates, identity) + capture portail.',
    impactIfFails: 'Workload compromis -> deploiement de malware, minage crypto, pivot vers d autres ressources.',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: '64d9450a-eea4-4036-b1c3-3a4d6db5dee8',
  },
  {
    id: 'AZ-CMP-004', family: 'compute', severity: 'medium',
    title: 'App Services ont authentification (Easy Auth) ou Front Door + WAF',
    description: 'Les App Services exposees ont une couche d auth : EasyAuth (Entra ID), API Management, ou Front Door + WAF en amont.',
    rationale: 'Defense en profondeur. Une App Service sans auth = surface d attaque large. Réduit drastiquement risque OWASP A07.',
    references: [
      { framework: 'ISO27001', ref: 'A.8.5',      label: 'Authentification sécurisée' },
      { framework: 'CyFun',    ref: 'PR.AC-1',    label: 'Identities verified', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '9.3',           label: 'App Service authentication enabled' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.Web/sites/{name}/config/authsettingsV2'],
    remediation: 'App Service > Authentication > Add identity provider > Microsoft. Configure tenant Entra ID. Require authentication = ON. OU mettre Front Door WAF en amont (preferable architecture).',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.Web%2Fsites',
    evidence: 'Export ARM de la configuration VM/App Service (disk encryption, system updates, identity) + capture portail.',
    impactIfFails: 'Workload compromis -> deploiement de malware, minage crypto, pivot vers d autres ressources.',
    effort: 'medium',
    frequency: 'monthly',
    defenderRecommendationId: '64d9450a-eea4-4036-b1c3-3a4d6db5dee8',
  },
  {
    id: 'AZ-CMP-005', family: 'compute', severity: 'medium',
    title: 'Patch management : VMs scan vulnerabilites + auto-update',
    description: 'Toutes les VMs ont : (a) Azure Update Manager configure, (b) Defender for Cloud agent vulnerability scan active, (c) patches critiques applies < 30 jours.',
    rationale: 'NIS2 Art. 21(2)(e) gestion vulnerabilites. Sans patch management : kit Metasploit + 1 CVE = compromission.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(e)', label: 'Gestion vulnerabilites' },
      { framework: 'ISO27001', ref: 'A.8.8',         label: 'Gestion vulnerabilites' },
      { framework: 'CyFun',    ref: 'PR.IP-12',      label: 'Vulnerability mgmt plan', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '7.5',           label: 'VM OS patching periodic' },
    ],
    armEndpoints: [
      'GET /subscriptions/{subId}/providers/Microsoft.Maintenance/configurationAssignments',
      'GET /subscriptions/{subId}/providers/Microsoft.Compute/virtualMachines (status.patchSummary)',
    ],
    remediation: 'Azure Update Manager (gratuit) : Configure auto-assessment + schedule patching mensuel pour patches Critical/Important. Defender for Servers P2 inclut vulnerability assessment via Qualys.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/Microsoft_Azure_Compute/UpdateManagerMenuBlade',
    evidence: 'Export ARM de la configuration VM/App Service (disk encryption, system updates, identity) + capture portail.',
    impactIfFails: 'Workload compromis -> deploiement de malware, minage crypto, pivot vers d autres ressources.',
    effort: 'medium',
    frequency: 'monthly',
  },
  {
    id: 'AZ-CMP-006', family: 'compute', severity: 'medium',
    title: 'Container Registries privatises (Private Endpoint ou tier Premium)',
    description: 'Les Azure Container Registries (ACR) hebergeant des images custom ont Private Endpoint OU Network restrictions (tier Premium requis).',
    rationale: 'Empeche pull/push d images depuis Internet. Combine avec scan vulnerabilites (Defender for Containers) = supply chain sécurisée.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(d)', label: 'Supply chain' },
      { framework: 'ISO27001', ref: 'A.5.22',        label: 'Sécurité supply chain' },
      { framework: 'CyFun',    ref: 'PR.AC-5',       label: 'Network integrity', cyfunLevel: 'Important' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.ContainerRegistry/registries'],
    remediation: 'ACR tier Premium > Networking > Public network access = Disabled > Add Private Endpoint sur le VNet d entreprise. Defender for Containers : scan images au push.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.ContainerRegistry%2Fregistries',
    evidence: 'Export ARM de la configuration VM/App Service (disk encryption, system updates, identity) + capture portail.',
    impactIfFails: 'Workload compromis -> deploiement de malware, minage crypto, pivot vers d autres ressources.',
    effort: 'medium',
    frequency: 'monthly',
    defenderRecommendationId: 'baa1a37c-7fb2-4cf4-b9d3-1c46f9aa6a5d',
  },

  // ==========================================================================
  //  KEY VAULT (AZ-KV-*) - 4 contrôles
  // ==========================================================================
  {
    id: 'AZ-KV-001', family: 'keyvault', severity: 'high',
    title: 'Key Vaults utilisent RBAC (pas Access Policies legacy)',
    description: 'Tous les Key Vaults ont enableRbacAuthorization = true. Modèle Access Policies legacy deprecated par Microsoft.',
    rationale: 'RBAC = audit + granularite + Entra ID centralise. Access Policies = ancien modèle moins traceable.',
    references: [
      { framework: 'ISO27001', ref: 'A.5.18',     label: 'Droits d accès' },
      { framework: 'CyFun',    ref: 'PR.AC-4',    label: 'Permissions managed', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '8.4',           label: 'Key Vault RBAC permission model' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.KeyVault/vaults'],
    remediation: 'Key Vault > Access configuration > Permission model = "Azure role-based access control". Re-créer les permissions via IAM avec les roles Key Vault (Reader, Secrets User, Crypto User, etc.).',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.KeyVault%2Fvaults',
    evidence: 'Export ARM de la Key Vault (access policies / RBAC, networkAcls, soft-delete, purge protection) + capture portail.',
    impactIfFails: 'Compromission de cles/secrets -> dechiffrement de données at-rest, usurpation d identite applicative.',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: '8606fc78-9f54-4c2a-a73f-12c40bf9cd9d',
  },
  {
    id: 'AZ-KV-002', family: 'keyvault', severity: 'high',
    title: 'Key Vaults ont Soft Delete + Purge Protection enabled',
    description: 'Tous les Key Vaults ont enableSoftDelete = true (90 jours min) et enablePurgeProtection = true (irreversible après activation).',
    rationale: 'Empeche destruction definitive de cles/secrets par erreur ou attaque malveillante. Pourrait briser des apps si certificat purgé par accident.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(h)', label: 'Cryptographie' },
      { framework: 'ISO27001', ref: 'A.8.13',        label: 'Sauvegarde' },
      { framework: 'CyFun',    ref: 'PR.IP-4',       label: 'Backups maintained', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '8.5',           label: 'Key Vault soft delete + purge protection' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.KeyVault/vaults'],
    remediation: 'Key Vault > Properties > Soft delete = ON (90 days retention) + Purge protection = ON. Attention : Purge Protection irreversible. Bien planifier.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.KeyVault%2Fvaults',
    evidence: 'Export ARM de la Key Vault (access policies / RBAC, networkAcls, soft-delete, purge protection) + capture portail.',
    impactIfFails: 'Compromission de cles/secrets -> dechiffrement de données at-rest, usurpation d identite applicative.',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: '88bbc99c-e5af-ddd7-6105-6150b2bfa519',
  },
  {
    id: 'AZ-KV-003', family: 'keyvault', severity: 'medium',
    title: 'Key Vaults envoient logs diagnostics vers Log Analytics',
    description: 'Tous les Key Vaults ont diagnostic settings configures : categorie "AuditEvent" envoyee vers Log Analytics + retention >= 90 jours.',
    rationale: 'Tracabilite accès aux secrets/cles. Indispensable pour audit incident + RGPD.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(b)', label: 'Gestion incidents' },
      { framework: 'ISO27001', ref: 'A.8.15',        label: 'Journalisation' },
      { framework: 'CyFun',    ref: 'DE.CM-1',       label: 'Network monitored', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '8.6',           label: 'Key Vault diagnostic logging' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.KeyVault/vaults/{name}/providers/Microsoft.Insights/diagnosticSettings'],
    remediation: 'Key Vault > Diagnostic settings > Add diagnostic setting > category AuditEvent > Send to Log Analytics workspace. Cost: minimal (volume écriture faible).',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.KeyVault%2Fvaults',
    evidence: 'Export ARM de la Key Vault (access policies / RBAC, networkAcls, soft-delete, purge protection) + capture portail.',
    impactIfFails: 'Compromission de cles/secrets -> dechiffrement de données at-rest, usurpation d identite applicative.',
    effort: 'medium',
    frequency: 'monthly',
    defenderRecommendationId: 'dbfc99e3-916b-4992-acdc-919b35eb3f5b',
  },
  {
    id: 'AZ-KV-004', family: 'keyvault', severity: 'medium',
    title: 'Toutes les Keys et Secrets ont une date d expiration',
    description: 'Aucune cle / secret / certificat dans les Key Vaults n a expiration = null. Rotation périodique imposee.',
    rationale: 'Empeche les secrets eternels qui finissent dans Git. Force rotation périodique.',
    references: [
      { framework: 'ISO27001', ref: 'A.8.24',     label: 'Gestion des cles' },
      { framework: 'CyFun',    ref: 'PR.DS-1',    label: 'Data at rest', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '8.2+8.3',           label: 'Keys + secrets expiration' },
    ],
    armEndpoints: [
      'GET /v7.4/keys (Key Vault data plane)',
      'GET /v7.4/secrets (Key Vault data plane)',
    ],
    remediation: 'Key Vault > Keys / Secrets / Certificates > Pour chaque entree : Set expiration date (12 mois par defaut). Documenter procédure de rotation.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.KeyVault%2Fvaults',
    evidence: 'Export ARM de la Key Vault (access policies / RBAC, networkAcls, soft-delete, purge protection) + capture portail.',
    impactIfFails: 'Compromission de cles/secrets -> dechiffrement de données at-rest, usurpation d identite applicative.',
    effort: 'medium',
    frequency: 'monthly',
  },

  // ==========================================================================
  //  LOGGING & MONITORING (AZ-LOG-*) - 5 contrôles
  // ==========================================================================
  {
    id: 'AZ-LOG-001', family: 'logging', severity: 'critical',
    title: 'Activity Log retention configuree >= 90 jours',
    description: 'L Activity Log de la subscription est exporte vers Storage / Log Analytics / Event Hub avec retention >= 90 jours (1 an recommande).',
    rationale: 'L Activity Log natif Azure : 90 jours rolling. Sans export : impossible d enqueter sur incident > 90 jours après. NIS2 Art. 21(2)(b).',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(b)', label: 'Gestion incidents - logs' },
      { framework: 'ISO27001', ref: 'A.8.15',        label: 'Journalisation' },
      { framework: 'CyFun',    ref: 'DE.CM-1',       label: 'Network monitored', cyfunLevel: 'Basic' },
      { framework: 'CIS-Azure', ref: '5.1',           label: 'Activity Log retention >= 90 days' },
    ],
    armEndpoints: [
      'GET /subscriptions/{subId}/providers/Microsoft.Insights/diagnosticSettings',
      'GET /subscriptions/{subId}/providers/Microsoft.Insights/logProfiles',
    ],
    remediation: 'Subscription > Activity log > Diagnostic settings > Add diagnostic setting. Categories : All. Destination : Log Analytics OU Storage avec retention 365 jours.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/Microsoft_Azure_Monitoring/AzureMonitoringBrowseBlade',
    evidence: 'Export JSON des Diagnostic Settings + screenshot du Log Analytics workspace avec query de presence des logs.',
    impactIfFails: 'Absence de traces -> incident non detecte, conformité NIS2 Art.23 non respectee (notification 24h impossible).',
    effort: 'low',
    frequency: 'continuous',
    defenderRecommendationId: 'dbfc99e3-916b-4992-acdc-919b35eb3f5b',
  },
  {
    id: 'AZ-LOG-002', family: 'logging', severity: 'high',
    title: 'Diagnostic Settings configures sur ressources critiques',
    description: 'Les ressources critiques (Key Vault, Storage, SQL DB, App Service, NSG flow logs) ont des Diagnostic Settings envoyant vers Log Analytics.',
    rationale: 'Sans logs diagnostics : aucune visibilite opérationnelle. Incident response aveugle.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(b)', label: 'Gestion incidents' },
      { framework: 'ISO27001', ref: 'A.8.15',        label: 'Journalisation' },
      { framework: 'CyFun',    ref: 'DE.CM-1',       label: 'Network monitored', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '5.1',           label: 'Resource diagnostic settings' },
    ],
    armEndpoints: ['GET /providers/Microsoft.Insights/diagnosticSettings (pour chaque ressource critique)'],
    remediation: 'Pour chaque ressource critique : Diagnostic settings > Add > Categories audit + critical > Destination Log Analytics. Idealement : Azure Policy initiative "Enable Azure Monitor for X".',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/Microsoft_Azure_Monitoring/AzureMonitoringBrowseBlade',
    evidence: 'Export JSON des Diagnostic Settings + screenshot du Log Analytics workspace avec query de presence des logs.',
    impactIfFails: 'Absence de traces -> incident non detecte, conformité NIS2 Art.23 non respectee (notification 24h impossible).',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: 'dbfc99e3-916b-4992-acdc-919b35eb3f5b',
  },
  {
    id: 'AZ-LOG-003', family: 'logging', severity: 'high',
    title: 'Microsoft Defender for Cloud en tier Standard (paye)',
    description: 'La subscription a Defender for Cloud "Plan 2" active sur au moins Servers + Storage + Key Vault + SQL.',
    rationale: 'Le tier "Free" ne fournit que les recommandations. Standard : threat détection, JIT, vulnerability assessment, file integrity monitoring.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(g)', label: 'Hygiene cyber' },
      { framework: 'ISO27001', ref: 'A.8.16',        label: 'Surveillance' },
      { framework: 'CyFun',    ref: 'DE.CM-4',       label: 'Malicious code detected', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '2.1',           label: 'Defender for Cloud Standard tier' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.Security/pricings'],
    remediation: 'Defender for Cloud > Environment settings > Subscription > Defender plans > Enable Plan 2 pour : Servers, Storage, Key Vault, SQL, Containers. Cout variable selon ressources.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/Microsoft_Azure_Security/SecurityMenuBlade/~/0',
    evidence: 'Export JSON des Diagnostic Settings + screenshot du Log Analytics workspace avec query de presence des logs.',
    impactIfFails: 'Absence de traces -> incident non detecte, conformité NIS2 Art.23 non respectee (notification 24h impossible).',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: 'b1cd27e0-2ecb-4222-9d27-542e7d3ec4d6',
  },
  {
    id: 'AZ-LOG-004', family: 'logging', severity: 'medium',
    title: 'Defender for Cloud auto-provisioning active',
    description: 'Les agents Defender (MMA/AMA, Log Analytics, Vulnerability Assessment) sont auto-provisionnes sur nouvelles ressources.',
    rationale: 'Couverture exhaustive sans intervention manuelle. Empeche les "trous" de protection sur ressources oubliees.',
    references: [
      { framework: 'ISO27001', ref: 'A.8.16',     label: 'Surveillance' },
      { framework: 'CyFun',    ref: 'DE.CM-7',    label: 'Monitoring for unauthorized', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '2.2+2.3',           label: 'Auto-provisioning agents + VA' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.Security/autoProvisioningSettings'],
    remediation: 'Defender for Cloud > Environment settings > Subscription > Auto provisioning > Toggle ON pour : Log Analytics agent for VMs, Vulnerability assessment, Microsoft Defender for Endpoint intégration.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/Microsoft_Azure_Security/SecurityMenuBlade/~/16',
    evidence: 'Export JSON des Diagnostic Settings + screenshot du Log Analytics workspace avec query de presence des logs.',
    impactIfFails: 'Absence de traces -> incident non detecte, conformité NIS2 Art.23 non respectee (notification 24h impossible).',
    effort: 'medium',
    frequency: 'monthly',
    defenderRecommendationId: 'b1cd27e0-2ecb-4222-9d27-542e7d3ec4d6',
  },
  {
    id: 'AZ-LOG-005', family: 'logging', severity: 'medium',
    title: 'Log Analytics workspace existe et est utilise (ingestion active)',
    description: 'Au moins 1 Log Analytics workspace existe, recoit des logs des 7 derniers jours (> 0 events ingested).',
    rationale: 'Centre nerveux du monitoring Azure. Sans LAW : pas de Sentinel, pas de queries cross-resource, pas d analytics.',
    references: [
      { framework: 'ISO27001', ref: 'A.8.15',     label: 'Journalisation' },
      { framework: 'CyFun',    ref: 'DE.CM-1',    label: 'Network monitored', cyfunLevel: 'Basic' },
      { framework: 'CIS-Azure', ref: '5.1',           label: 'Log Analytics workspace exists' },
    ],
    armEndpoints: [
      'GET /subscriptions/{subId}/providers/Microsoft.OperationalInsights/workspaces',
      'POST /v1/workspaces/{workspaceId}/query (count Heartbeat)',
    ],
    remediation: 'Si pas de LAW : créer 1 LAW central par region. Configurer retention (90 jours minimum, 1 an recommande). Pointer les Diagnostic Settings dessus.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.OperationalInsights%2Fworkspaces',
    evidence: 'Export JSON des Diagnostic Settings + screenshot du Log Analytics workspace avec query de presence des logs.',
    impactIfFails: 'Absence de traces -> incident non detecte, conformité NIS2 Art.23 non respectee (notification 24h impossible).',
    effort: 'medium',
    frequency: 'monthly',
    defenderRecommendationId: 'dbfc99e3-916b-4992-acdc-919b35eb3f5b',
  },

  // ==========================================================================
  //  GOVERNANCE & POLICY (AZ-GOV-*) - 5 contrôles
  // ==========================================================================
  {
    id: 'AZ-GOV-001', family: 'governance', severity: 'high',
    title: 'Azure Policy initiative assignee (CIS / Azure Security Benchmark)',
    description: 'Au moins une initiative Policy de sécurité est assignee a la subscription (CIS Microsoft Azure Foundations Benchmark, Azure Security Benchmark, ou ISO 27001).',
    rationale: 'Sans Policy : les nouvelles ressources peuvent être non-conformes des leur creation. Policy = automatic compliance enforcement.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(1)',    label: 'Mesures proportionnées' },
      { framework: 'ISO27001', ref: 'A.5.1',         label: 'Politiques sécurité' },
      { framework: 'CyFun',    ref: 'ID.GV-1',       label: 'Policy established', cyfunLevel: 'Important' },
    ],
    armEndpoints: [
      'GET /subscriptions/{subId}/providers/Microsoft.Authorization/policyAssignments',
      'GET /providers/Microsoft.Authorization/policySetDefinitions',
    ],
    remediation: 'Azure Policy > Definitions > Initiatives > Search "CIS Azure" > Assign to subscription. Mode "Enforce" pour les contrôles non-disruptifs, "Audit" pour le reste.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/Microsoft_Azure_Policy/PolicyMenuBlade',
    evidence: 'Export Azure Policy assignments + locks + capture portail Subscriptions > Resource Locks.',
    impactIfFails: 'Derive de configuration non controlee -> non-conformité réglementaire, audits externes en echec.',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: '2e6d966e-91e1-4f7d-9b35-de00b5e07e3f',
  },
  {
    id: 'AZ-GOV-002', family: 'governance', severity: 'medium',
    title: 'Resource Tags pour ownership / cost center documentes',
    description: 'Au moins 80% des ressources de la subscription ont les tags requis : Environment, Owner, CostCenter, Criticality.',
    rationale: 'Tags critiques pour : facturation, ownership en cas d incident, automatisation (snapshot/backup policies par tag).',
    references: [
      { framework: 'ISO27001', ref: 'A.5.9',      label: 'Inventaire des actifs' },
      { framework: 'CyFun',    ref: 'ID.AM-6',    label: 'Cybersecurity roles', cyfunLevel: 'Important' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/resources'],
    remediation: 'Définir tagging policy (Environment, Owner, CostCenter, Criticality). Implementer via Azure Policy "Require tag" + initiation tag inheritance depuis Resource Group.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseAll',
    evidence: 'Export Azure Policy assignments + locks + capture portail Subscriptions > Resource Locks.',
    impactIfFails: 'Derive de configuration non controlee -> non-conformité réglementaire, audits externes en echec.',
    effort: 'medium',
    frequency: 'monthly',
  },
  {
    id: 'AZ-GOV-003', family: 'governance', severity: 'medium',
    title: 'Subscription a un contact sécurité documente',
    description: 'La subscription a au moins 1 contact sécurité configure dans Defender for Cloud (email + phone), alerts envoyees.',
    rationale: 'Permet a Microsoft / Defender de notifier en cas d incident detecte sur la subscription. Exigence MS Trust Center.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 23',     label: 'Notifications' },
      { framework: 'ISO27001', ref: 'A.5.5',       label: 'Contact avec autorites' },
      { framework: 'CyFun',    ref: 'RS.CO-1',     label: 'Personnel roles', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '2.4',           label: 'Defender email notifications configured' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.Security/securityContacts'],
    remediation: 'Defender for Cloud > Environment settings > Subscription > Email notifications. Add : email Security team + Owner phone. Notify on : Severity High+.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/Microsoft_Azure_Security/SecurityMenuBlade',
    evidence: 'Export Azure Policy assignments + locks + capture portail Subscriptions > Resource Locks.',
    impactIfFails: 'Derive de configuration non controlee -> non-conformité réglementaire, audits externes en echec.',
    effort: 'medium',
    frequency: 'monthly',
  },
  {
    id: 'AZ-GOV-004', family: 'governance', severity: 'medium',
    title: 'Resource Locks (CanNotDelete / ReadOnly) sur ressources critiques',
    description: 'Les ressources critiques (Key Vault, Storage avec prod data, VNet hub, SQL DB prod) ont des Locks niveau "CanNotDelete" minimum.',
    rationale: 'Empeche suppression accidentelle ou malveillante. Un attaquant Owner peut supprimer tout en 1 minute sans Lock.',
    references: [
      { framework: 'ISO27001', ref: 'A.5.10',     label: 'Usage acceptable' },
      { framework: 'CyFun',    ref: 'PR.IP-1',    label: 'Baseline configurations', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '5.3',           label: 'Resource Locks on critical resources' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.Authorization/locks'],
    remediation: 'Pour chaque ressource critique : Settings > Locks > Add > Lock type "CanNotDelete" OU "ReadOnly". Justification documentée.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/HubsExtension/BrowseAll',
    evidence: 'Export Azure Policy assignments + locks + capture portail Subscriptions > Resource Locks.',
    impactIfFails: 'Derive de configuration non controlee -> non-conformité réglementaire, audits externes en echec.',
    effort: 'medium',
    frequency: 'monthly',
    defenderRecommendationId: 'ada5fb19-1ef4-c121-32fa-9c6f4ccb13d4',
  },
  {
    id: 'AZ-GOV-005', family: 'governance', severity: 'high',
    title: 'Defender for Cloud Secure Score >= 70%',
    description: 'Le Secure Score de Defender for Cloud est >= 70% sur l ensemble de la subscription (compromis sécurité/usage).',
    rationale: 'Indicateur synthetique Microsoft. < 50% = posture faible. > 70% = posture correcte. 90%+ = best-in-class.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(1)',    label: 'Mesures proportionnées' },
      { framework: 'ISO27001', ref: 'Clause 9.1',    label: 'Surveillance, mesures' },
      { framework: 'CyFun',    ref: 'DE.CM-7',       label: 'Monitoring', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '2.1',           label: 'Defender Secure Score >= 70%' },
    ],
    armEndpoints: ['GET /subscriptions/{subId}/providers/Microsoft.Security/secureScores'],
    remediation: 'Defender for Cloud > Secure score > Recommandations > Trier par "Score increase" > Resoudre les top 5 contrôles. Iterer mensuellement.',
    manual: false,
    azurePortalUrl: 'https://portal.azure.com/#view/Microsoft_Azure_Security/SecurityMenuBlade',
    evidence: 'Export Azure Policy assignments + locks + capture portail Subscriptions > Resource Locks.',
    impactIfFails: 'Derive de configuration non controlee -> non-conformité réglementaire, audits externes en echec.',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: 'b1cd27e0-2ecb-4222-9d27-542e7d3ec4d6',
  },

  // ============================================================================
  //  Phase 50.C/D/E - 27 nouveaux contrôles CIS Azure Foundations v2.1
  //   - 50.C : Storage/SQL/AppService gaps (10)
  //   - 50.D : Defender for Cloud plans (8)
  //   - 50.E : AppService complet (9)
  // ============================================================================
  {
    id: 'AZ-DAT-008', family: 'data', severity: 'high',
    title: 'Storage account keys regenerated periodically',
    description: 'Cle d accès Storage rotates a une période <= 90 jours.',
    rationale: 'CIS Azure Foundations v2.1 3.2 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(j)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'PR.AC-1',    label: 'CyFun PR.AC-1 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '3.2',      label: 'Storage account keys regenerated periodically' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 3.2.',
    manual: false,
    evidence: 'Export JSON des proprietes ressource (encryption, network ACLs, retention) + capture portail Storage/SQL/Cosmos.',
    impactIfFails: 'Fuite ou alteration de données clients -> sanctions RGPD, NIS2 (jusqu a 10 M EUR), perte de confiance.',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: '8c3e93d3-0276-4d06-b20a-9a9f3012742c',
  },
  {
    id: 'AZ-DAT-009', family: 'data', severity: 'medium',
    title: 'Storage Queue/Table/Blob logging enabled',
    description: 'Storage Analytics Logging actif pour Queue, Table, Blob (au moins read + write + delete).',
    rationale: 'CIS Azure Foundations v2.1 3.3 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(b)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'DE.AE-3',    label: 'CyFun DE.AE-3 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '3.3',      label: 'Storage Queue/Table/Blob logging enabled' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 3.3.',
    manual: false,
    evidence: 'Export JSON des proprietes ressource (encryption, network ACLs, retention) + capture portail Storage/SQL/Cosmos.',
    impactIfFails: 'Fuite ou alteration de données clients -> sanctions RGPD, NIS2 (jusqu a 10 M EUR), perte de confiance.',
    effort: 'medium',
    frequency: 'monthly',
    defenderRecommendationId: '8c3e93d3-0276-4d06-b20a-9a9f3012742c',
  },
  {
    id: 'AZ-DAT-010', family: 'data', severity: 'high',
    title: 'Storage default deny network access',
    description: 'Storage Account avec defaultAction = Deny + bypass = AzureServices uniquement.',
    rationale: 'CIS Azure Foundations v2.1 3.6 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(j)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'PR.AC-5',    label: 'CyFun PR.AC-5 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '3.6',      label: 'Storage default deny network access' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 3.6.',
    manual: false,
    evidence: 'Export JSON des proprietes ressource (encryption, network ACLs, retention) + capture portail Storage/SQL/Cosmos.',
    impactIfFails: 'Fuite ou alteration de données clients -> sanctions RGPD, NIS2 (jusqu a 10 M EUR), perte de confiance.',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: '8c3e93d3-0276-4d06-b20a-9a9f3012742c',
  },
  {
    id: 'AZ-DAT-011', family: 'data', severity: 'high',
    title: 'SQL Azure AD admin set',
    description: 'Chaque SQL Server a un Azure AD admin défini (vs SQL auth seul).',
    rationale: 'CIS Azure Foundations v2.1 4.4 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(j)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'PR.AC-1',    label: 'CyFun PR.AC-1 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '4.4',      label: 'SQL Azure AD admin set' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 4.4.',
    manual: false,
    evidence: 'Export JSON des proprietes ressource (encryption, network ACLs, retention) + capture portail Storage/SQL/Cosmos.',
    impactIfFails: 'Fuite ou alteration de données clients -> sanctions RGPD, NIS2 (jusqu a 10 M EUR), perte de confiance.',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: 'f8262030-eafe-4d05-87f4-0fbf85a05c1e',
  },
  {
    id: 'AZ-DAT-012', family: 'data', severity: 'high',
    title: 'SQL TDE with Customer Managed Key',
    description: 'SQL Database TDE avec CMK plutot que Microsoft-managed key.',
    rationale: 'CIS Azure Foundations v2.1 4.5 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(h)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'PR.DS-1',    label: 'CyFun PR.DS-1 (Essential)', cyfunLevel: 'Essential' },
      { framework: 'CIS-Azure', ref: '4.5',      label: 'SQL TDE with Customer Managed Key' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 4.5.',
    manual: false,
    evidence: 'Export JSON des proprietes ressource (encryption, network ACLs, retention) + capture portail Storage/SQL/Cosmos.',
    impactIfFails: 'Fuite ou alteration de données clients -> sanctions RGPD, NIS2 (jusqu a 10 M EUR), perte de confiance.',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: 'f8262030-eafe-4d05-87f4-0fbf85a05c1e',
  },
  {
    id: 'AZ-DAT-013', family: 'data', severity: 'high',
    title: 'SQL Advanced Threat Protection enabled',
    description: 'SQL Server Defender for SQL active sur tous les serveurs avec données sensibles.',
    rationale: 'CIS Azure Foundations v2.1 4.8 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(b)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'DE.AE-2',    label: 'CyFun DE.AE-2 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '4.8',      label: 'SQL Advanced Threat Protection enabled' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 4.8.',
    manual: false,
    evidence: 'Export JSON des proprietes ressource (encryption, network ACLs, retention) + capture portail Storage/SQL/Cosmos.',
    impactIfFails: 'Fuite ou alteration de données clients -> sanctions RGPD, NIS2 (jusqu a 10 M EUR), perte de confiance.',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: 'f8262030-eafe-4d05-87f4-0fbf85a05c1e',
  },
  {
    id: 'AZ-DAT-014', family: 'data', severity: 'medium',
    title: 'PostgreSQL ssl_enforce = ON',
    description: 'Azure Database for PostgreSQL Flexible Server avec require_secure_transport = on.',
    rationale: 'CIS Azure Foundations v2.1 4.7 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(h)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'PR.DS-2',    label: 'CyFun PR.DS-2 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '4.7',      label: 'PostgreSQL ssl_enforce = ON' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 4.7.',
    manual: false,
    evidence: 'Export JSON des proprietes ressource (encryption, network ACLs, retention) + capture portail Storage/SQL/Cosmos.',
    impactIfFails: 'Fuite ou alteration de données clients -> sanctions RGPD, NIS2 (jusqu a 10 M EUR), perte de confiance.',
    effort: 'medium',
    frequency: 'monthly',
    defenderRecommendationId: 'f8262030-eafe-4d05-87f4-0fbf85a05c1e',
  },
  {
    id: 'AZ-CMP-007', family: 'compute', severity: 'medium',
    title: 'AppService TLS minimum 1.2',
    description: 'AppService et Function App ont minTlsVersion = 1.2 minimum.',
    rationale: 'CIS Azure Foundations v2.1 9.2 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(h)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'PR.DS-2',    label: 'CyFun PR.DS-2 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '9.2',      label: 'AppService TLS minimum 1.2' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 9.2.',
    manual: false,
    evidence: 'Export ARM de la configuration VM/App Service (disk encryption, system updates, identity) + capture portail.',
    impactIfFails: 'Workload compromis -> deploiement de malware, minage crypto, pivot vers d autres ressources.',
    effort: 'medium',
    frequency: 'monthly',
    defenderRecommendationId: '7926d010-c8ca-4dbf-8d2b-3a8b3da1b32e',
  },
  {
    id: 'AZ-CMP-008', family: 'compute', severity: 'high',
    title: 'AppService Managed Identity',
    description: 'AppService utilise System-Assigned ou User-Assigned MI plutot que connectionString avec secrets.',
    rationale: 'CIS Azure Foundations v2.1 9.4 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(j)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'PR.AC-1',    label: 'CyFun PR.AC-1 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '9.4',      label: 'AppService Managed Identity' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 9.4.',
    manual: false,
    evidence: 'Export ARM de la configuration VM/App Service (disk encryption, system updates, identity) + capture portail.',
    impactIfFails: 'Workload compromis -> deploiement de malware, minage crypto, pivot vers d autres ressources.',
    effort: 'medium',
    frequency: 'weekly',
  },
  {
    id: 'AZ-DAT-015', family: 'data', severity: 'medium',
    title: 'Storage Activity Log Alerts',
    description: 'Activity Log Alerts pour Storage Account create/update + key regeneration.',
    rationale: 'CIS Azure Foundations v2.1 3.9 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(b)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'DE.AE-2',    label: 'CyFun DE.AE-2 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '3.9',      label: 'Storage Activity Log Alerts' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 3.9.',
    manual: false,
    evidence: 'Export JSON des proprietes ressource (encryption, network ACLs, retention) + capture portail Storage/SQL/Cosmos.',
    impactIfFails: 'Fuite ou alteration de données clients -> sanctions RGPD, NIS2 (jusqu a 10 M EUR), perte de confiance.',
    effort: 'medium',
    frequency: 'monthly',
    defenderRecommendationId: '8c3e93d3-0276-4d06-b20a-9a9f3012742c',
  },
  {
    id: 'AZ-LOG-006', family: 'logging', severity: 'high',
    title: 'Defender for Containers enabled',
    description: 'Defender for Containers actif (AKS + container registries scanning).',
    rationale: 'CIS Azure Foundations v2.1 2.1.1 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(b)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'DE.CM-1',    label: 'CyFun DE.CM-1 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '2.1.1',      label: 'Defender for Containers enabled' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 2.1.1.',
    manual: false,
    evidence: 'Export JSON des Diagnostic Settings + screenshot du Log Analytics workspace avec query de presence des logs.',
    impactIfFails: 'Absence de traces -> incident non detecte, conformité NIS2 Art.23 non respectee (notification 24h impossible).',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: 'b1cd27e0-2ecb-4222-9d27-542e7d3ec4d6',
  },
  {
    id: 'AZ-LOG-007', family: 'logging', severity: 'high',
    title: 'Defender for Databases enabled',
    description: 'Defender for SQL + Defender for Open-source databases actif.',
    rationale: 'CIS Azure Foundations v2.1 2.1.2 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(b)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'DE.CM-1',    label: 'CyFun DE.CM-1 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '2.1.2',      label: 'Defender for Databases enabled' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 2.1.2.',
    manual: false,
    evidence: 'Export JSON des Diagnostic Settings + screenshot du Log Analytics workspace avec query de presence des logs.',
    impactIfFails: 'Absence de traces -> incident non detecte, conformité NIS2 Art.23 non respectee (notification 24h impossible).',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: 'b1cd27e0-2ecb-4222-9d27-542e7d3ec4d6',
  },
  {
    id: 'AZ-LOG-008', family: 'logging', severity: 'high',
    title: 'Defender for Storage enabled',
    description: 'Defender for Storage actif avec Malware Scanning + Sensitive Data Discovery.',
    rationale: 'CIS Azure Foundations v2.1 2.1.3 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(b)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'DE.CM-1',    label: 'CyFun DE.CM-1 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '2.1.3',      label: 'Defender for Storage enabled' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 2.1.3.',
    manual: false,
    evidence: 'Export JSON des Diagnostic Settings + screenshot du Log Analytics workspace avec query de presence des logs.',
    impactIfFails: 'Absence de traces -> incident non detecte, conformité NIS2 Art.23 non respectee (notification 24h impossible).',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: '8c3e93d3-0276-4d06-b20a-9a9f3012742c',
  },
  {
    id: 'AZ-LOG-009', family: 'logging', severity: 'high',
    title: 'Defender for AppService enabled',
    description: 'Defender for AppService actif (détection web shells, exploits courants).',
    rationale: 'CIS Azure Foundations v2.1 2.1.4 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(b)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'DE.CM-1',    label: 'CyFun DE.CM-1 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '2.1.4',      label: 'Defender for AppService enabled' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 2.1.4.',
    manual: false,
    evidence: 'Export JSON des Diagnostic Settings + screenshot du Log Analytics workspace avec query de presence des logs.',
    impactIfFails: 'Absence de traces -> incident non detecte, conformité NIS2 Art.23 non respectee (notification 24h impossible).',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: 'b1cd27e0-2ecb-4222-9d27-542e7d3ec4d6',
  },
  {
    id: 'AZ-LOG-010', family: 'logging', severity: 'high',
    title: 'Defender for Key Vault enabled',
    description: 'Defender for Key Vault actif (anomaly détection sur accès secrets).',
    rationale: 'CIS Azure Foundations v2.1 2.1.5 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(b)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'DE.CM-1',    label: 'CyFun DE.CM-1 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '2.1.5',      label: 'Defender for Key Vault enabled' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 2.1.5.',
    manual: false,
    evidence: 'Export JSON des Diagnostic Settings + screenshot du Log Analytics workspace avec query de presence des logs.',
    impactIfFails: 'Absence de traces -> incident non detecte, conformité NIS2 Art.23 non respectee (notification 24h impossible).',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: 'b1cd27e0-2ecb-4222-9d27-542e7d3ec4d6',
  },
  {
    id: 'AZ-LOG-011', family: 'logging', severity: 'high',
    title: 'Defender for Resource Manager enabled',
    description: 'Defender for Resource Manager actif (alertes sur deployments suspects).',
    rationale: 'CIS Azure Foundations v2.1 2.1.6 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(b)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'DE.AE-3',    label: 'CyFun DE.AE-3 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '2.1.6',      label: 'Defender for Resource Manager enabled' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 2.1.6.',
    manual: false,
    evidence: 'Export JSON des Diagnostic Settings + screenshot du Log Analytics workspace avec query de presence des logs.',
    impactIfFails: 'Absence de traces -> incident non detecte, conformité NIS2 Art.23 non respectee (notification 24h impossible).',
    effort: 'medium',
    frequency: 'weekly',
    defenderRecommendationId: 'b1cd27e0-2ecb-4222-9d27-542e7d3ec4d6',
  },
  {
    id: 'AZ-LOG-012', family: 'logging', severity: 'medium',
    title: 'Defender for DNS enabled',
    description: 'Defender for DNS actif (détection DGA, C2 callbacks).',
    rationale: 'CIS Azure Foundations v2.1 2.1.7 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(b)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'DE.CM-1',    label: 'CyFun DE.CM-1 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '2.1.7',      label: 'Defender for DNS enabled' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 2.1.7.',
    manual: false,
    evidence: 'Export JSON des Diagnostic Settings + screenshot du Log Analytics workspace avec query de presence des logs.',
    impactIfFails: 'Absence de traces -> incident non detecte, conformité NIS2 Art.23 non respectee (notification 24h impossible).',
    effort: 'medium',
    frequency: 'monthly',
    defenderRecommendationId: 'b1cd27e0-2ecb-4222-9d27-542e7d3ec4d6',
  },
  {
    id: 'AZ-LOG-013', family: 'logging', severity: 'medium',
    title: 'Microsoft Cloud Security Benchmark applied',
    description: 'MCSB Initiative applique au niveau Subscription + autres standards regulatoires si requis.',
    rationale: 'CIS Azure Foundations v2.1 2.5 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(a)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'ID.GV-3',    label: 'CyFun ID.GV-3 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '2.5',      label: 'Microsoft Cloud Security Benchmark applied' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 2.5.',
    manual: false,
    evidence: 'Export JSON des Diagnostic Settings + screenshot du Log Analytics workspace avec query de presence des logs.',
    impactIfFails: 'Absence de traces -> incident non detecte, conformité NIS2 Art.23 non respectee (notification 24h impossible).',
    effort: 'medium',
    frequency: 'monthly',
  },
  {
    id: 'AZ-CMP-009', family: 'compute', severity: 'medium',
    title: 'AppService client cert mode',
    description: 'AppService avec clientCertEnabled = true + clientCertMode = Required pour APIs critiques.',
    rationale: 'CIS Azure Foundations v2.1 9.5 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(j)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'PR.AC-7',    label: 'CyFun PR.AC-7 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '9.5',      label: 'AppService client cert mode' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 9.5.',
    manual: false,
    evidence: 'Export ARM de la configuration VM/App Service (disk encryption, system updates, identity) + capture portail.',
    impactIfFails: 'Workload compromis -> deploiement de malware, minage crypto, pivot vers d autres ressources.',
    effort: 'medium',
    frequency: 'monthly',
  },
  {
    id: 'AZ-CMP-010', family: 'compute', severity: 'low',
    title: 'AppService auto-heal enabled',
    description: 'AppService avec auto-heal rules pour resilience automatique sur memory leaks ou hangs.',
    rationale: 'CIS Azure Foundations v2.1 9.6 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(e)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'RC.RP-1',    label: 'CyFun RC.RP-1 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '9.6',      label: 'AppService auto-heal enabled' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 9.6.',
    manual: false,
    evidence: 'Export ARM de la configuration VM/App Service (disk encryption, system updates, identity) + capture portail.',
    impactIfFails: 'Workload compromis -> deploiement de malware, minage crypto, pivot vers d autres ressources.',
    effort: 'low',
    frequency: 'quarterly',
  },
  {
    id: 'AZ-CMP-011', family: 'compute', severity: 'medium',
    title: 'AppService runtime version current',
    description: 'AppService runtime stack (Python/Node/Java/.NET) avec version supportee (pas EOL).',
    rationale: 'CIS Azure Foundations v2.1 9.7 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(e)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'PR.IP-12',    label: 'CyFun PR.IP-12 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '9.7',      label: 'AppService runtime version current' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 9.7.',
    manual: false,
    evidence: 'Export ARM de la configuration VM/App Service (disk encryption, system updates, identity) + capture portail.',
    impactIfFails: 'Workload compromis -> deploiement de malware, minage crypto, pivot vers d autres ressources.',
    effort: 'medium',
    frequency: 'monthly',
  },
  {
    id: 'AZ-CMP-012', family: 'compute', severity: 'high',
    title: 'AppService FTP State disabled or FTPS only',
    description: 'AppService ftpsState = Disabled (preferable) ou FtpsOnly (jamais AllAllowed).',
    rationale: 'CIS Azure Foundations v2.1 9.8 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(j)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'PR.AC-5',    label: 'CyFun PR.AC-5 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '9.8',      label: 'AppService FTP State disabled or FTPS only' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 9.8.',
    manual: false,
    evidence: 'Export ARM de la configuration VM/App Service (disk encryption, system updates, identity) + capture portail.',
    impactIfFails: 'Workload compromis -> deploiement de malware, minage crypto, pivot vers d autres ressources.',
    effort: 'medium',
    frequency: 'weekly',
  },
  {
    id: 'AZ-CMP-013', family: 'compute', severity: 'low',
    title: 'AppService HTTP 2.0 enabled',
    description: 'AppService http20Enabled = true pour performance + compatibilite moderne.',
    rationale: 'CIS Azure Foundations v2.1 9.9 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(h)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'PR.DS-2',    label: 'CyFun PR.DS-2 (Basic)', cyfunLevel: 'Basic' },
      { framework: 'CIS-Azure', ref: '9.9',      label: 'AppService HTTP 2.0 enabled' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 9.9.',
    manual: false,
    evidence: 'Export ARM de la configuration VM/App Service (disk encryption, system updates, identity) + capture portail.',
    impactIfFails: 'Workload compromis -> deploiement de malware, minage crypto, pivot vers d autres ressources.',
    effort: 'low',
    frequency: 'quarterly',
  },
  {
    id: 'AZ-CMP-014', family: 'compute', severity: 'medium',
    title: 'AppService web sockets et remote debug off en prod',
    description: 'AppService webSocketsEnabled selon besoin, remoteDebuggingEnabled = false en production.',
    rationale: 'CIS Azure Foundations v2.1 9.x - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(e)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'PR.IP-1',    label: 'CyFun PR.IP-1 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '9.x',      label: 'AppService web sockets et remote debug off en prod' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 9.x.',
    manual: false,
    evidence: 'Export ARM de la configuration VM/App Service (disk encryption, system updates, identity) + capture portail.',
    impactIfFails: 'Workload compromis -> deploiement de malware, minage crypto, pivot vers d autres ressources.',
    effort: 'medium',
    frequency: 'monthly',
  },
  {
    id: 'AZ-CMP-015', family: 'compute', severity: 'medium',
    title: 'AppService scaling et instance count',
    description: 'AppService Plan avec >= 2 instances pour disponibilite (sauf dev/sandbox).',
    rationale: 'CIS Azure Foundations v2.1 9.x - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(a)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'PR.PT-5',    label: 'CyFun PR.PT-5 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '9.x',      label: 'AppService scaling et instance count' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 9.x.',
    manual: false,
    evidence: 'Export ARM de la configuration VM/App Service (disk encryption, system updates, identity) + capture portail.',
    impactIfFails: 'Workload compromis -> deploiement de malware, minage crypto, pivot vers d autres ressources.',
    effort: 'medium',
    frequency: 'monthly',
  },
  {
    id: 'AZ-NET-008', family: 'network', severity: 'medium',
    title: 'Network Watcher Flow Logs configures',
    description: 'NSG Flow Logs avec retention >= 30j envoyes vers Log Analytics (traffic analytics).',
    rationale: 'CIS Azure Foundations v2.1 6.x - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(b)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'DE.CM-1',    label: 'CyFun DE.CM-1 (Important)', cyfunLevel: 'Important' },
      { framework: 'CIS-Azure', ref: '6.x',      label: 'Network Watcher Flow Logs configures' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 6.x.',
    manual: false,
    evidence: 'Export ARM de la configuration (NSG rules, Bastion config, JIT requests) + capture portail Network Security.',
    impactIfFails: 'Exposition réseau directe -> reconnaissance et pivot d attaquant facilites, services compromis.',
    effort: 'medium',
    frequency: 'monthly',
  },
  {
    id: 'AZ-KV-005', family: 'keyvault', severity: 'medium',
    title: 'Key Vault Private Endpoint',
    description: 'Key Vault avec Private Endpoint + publicNetworkAccess = Disabled.',
    rationale: 'CIS Azure Foundations v2.1 8.7 - exigence directe.',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(j)', label: 'Mesure technique NIS2' },
      { framework: 'ISO27001',  ref: 'A.5.x',          label: 'Contrôle ISO applicable' },
      { framework: 'CyFun',     ref: 'PR.AC-5',    label: 'CyFun PR.AC-5 (Essential)', cyfunLevel: 'Essential' },
      { framework: 'CIS-Azure', ref: '8.7',      label: 'Key Vault Private Endpoint' },
    ],
    armEndpoints: ['ARM REST - voir Recommendation'],
    remediation: 'Voir documentation CIS Azure Foundations Benchmark v2.1 section 8.7.',
    manual: false,
    evidence: 'Export ARM de la Key Vault (access policies / RBAC, networkAcls, soft-delete, purge protection) + capture portail.',
    impactIfFails: 'Compromission de cles/secrets -> dechiffrement de données at-rest, usurpation d identite applicative.',
    effort: 'medium',
    frequency: 'monthly',
    defenderRecommendationId: '88bbc99c-e5af-ddd7-6105-6150b2bfa519',
  },
];

// ============================================================================
//                           HELPERS
// ============================================================================

export function getAzureControlById(id: string): AzureControlDefinition | undefined {
  return AZURE_CONTROLS.find((c) => c.id === id);
}

export function getAzureControlsByFamily(family: AzureFamily): AzureControlDefinition[] {
  return AZURE_CONTROLS.filter((c) => c.family === family);
}

/** Ordre d affichage canonique des familles dans l UI (du plus critique au moins). */
export const AZURE_FAMILY_ORDER: AzureFamily[] = [
  'identity',
  'network',
  'data',
  'compute',
  'keyvault',
  'logging',
  'governance',
];

/** Couleurs pastel par famille (UI : badges, headers, etc.). */
export const AZURE_FAMILY_COLORS: Record<AzureFamily, { fg: string; bg: string }> = {
  identity:   { fg: '#1F3D5C', bg: '#E1ECF8' },
  network:    { fg: '#2E8B96', bg: '#E8F5F6' },
  data:       { fg: '#5A2A86', bg: '#F3E8FA' },
  compute:    { fg: '#8B4513', bg: '#FFF1E0' },
  keyvault:   { fg: '#B45309', bg: '#FFF8E6' },
  logging:    { fg: '#1F7A45', bg: '#E6F4EC' },
  governance: { fg: '#7A8B9C', bg: '#F7FAFB' },
};
